"use client";

import { Typography } from "@/components/ui/typography";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import React, { memo, useState } from "react";
import { cn } from "@/lib/utils";
import ArrowLink from "@/components/ui/arrow-link";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES, Text } from "@contentful/rich-text-types";
import { Content } from "@/lib/getDocumentsAndDeliverables";

export type FaqStackWithContentfulProps = {
  content: Pick<Content, "faq">;
} & Omit<React.ComponentProps<"div">, "content">;

const FaqStackWithContentful = ({
  content,
  className,
}: FaqStackWithContentfulProps) => {
  const [activeAccordion, setActiveAccordion] = useState<string>("item-0");

  return (
    <Accordion
      type="single"
      collapsible
      value={activeAccordion}
      onValueChange={setActiveAccordion}
      className={cn(
        "grid grid-cols-1 gap-2 divide-y divide-gray-300",
        className
      )}
    >
      {content.faq?.map((item, idx) => {
        const { faqQuestion, faqAnswer, faqUrl, faqUrlName } = item.fields;
        const isLast = idx === content.faq!.length - 1;

        return (
          <AccordionItem
            key={idx}
            value={`item-${idx}`}
            className={cn("!mt-0 !mb-0", {
              "border-b border-neutral-200 lg:border-gray-100": isLast,
            })}
            itemScope
            itemType="https://schema.org/Question"
          >
            <AccordionTrigger className="cursor-pointer font-semibold hover:no-underline flex justify-between lg:!py-3">
              <Typography
                variant="titleSm"
                className="font-semibold lg:text-xl lg:leading-[130%] lg:font-medium"
                asChild
              >
                <h4 itemProp="name">{faqQuestion}</h4>
              </Typography>
            </AccordionTrigger>

            <AccordionContent 
              className="text-semantic-text-primary lg:text-[#666666] text-base leading-[160%] pt-2 lg:pt-0 pb-6 lg:pb-3"
              itemScope
              itemType="https://schema.org/Answer"
              itemProp="acceptedAnswer"
            >
              <div className="space-y-4 leading-[160%] lg:leading-[145%] [&>p>strong]:font-semibold" itemProp="text">
                {documentToReactComponents(faqAnswer, {
                  renderNode: {
                    [BLOCKS.PARAGRAPH]: (node, children) => {
                      const arrChildren = React.Children.toArray(children);
                      const isEmpty =
                        arrChildren.length === 0 ||
                        arrChildren.every(
                          (c) => typeof c === "string" && c.trim() === ""
                        );

                      if (isEmpty) return null;

                      return <p>{children}</p>;
                    },
                    [BLOCKS.UL_LIST]: (node, children) => (
                      <ul className="list-disc ml-5 mb-2">{children}</ul>
                    ),
                    [BLOCKS.OL_LIST]: (node, children) => (
                      <ol className="list-decimal list-inside mb-2">
                        {children}
                      </ol>
                    ),
                    [BLOCKS.LIST_ITEM]: (node, children) => {
                      const arrChildren = React.Children.toArray(children).map(
                        (child) =>
                          typeof child === "string" ? child.trim() : child
                      );
                      return <li className="mb-1">{arrChildren}</li>;
                    },
                    [BLOCKS.QUOTE]: (node, children) => (
                      <blockquote className="border-l-4 pl-4 italic">
                        {children}
                      </blockquote>
                    ),
                    [INLINES.HYPERLINK]: (node) => {
                      const url = node.data.uri;
                      const textNode = node.content[0] as Text;
                      return (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline hover:text-blue-800 cursor-pointer"
                        >
                          {textNode.value}
                        </a>
                      );
                    },
                  },
                  renderText: (text) =>
                    text.split("\n").reduce((children, segment, index) => {
                      return [
                        ...children,
                        index > 0 && <br key={index} />,
                        segment,
                      ];
                    }, [] as React.ReactNode[]),
                })}
              </div>
              {faqUrl && faqUrlName && (
                <div className="mt-2 md:mt-4 flex">
                  <ArrowLink href={faqUrl}>{faqUrlName}</ArrowLink>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};

export default memo(FaqStackWithContentful);
